import type { ComponentType } from "react";

import type { Dayjs } from "dayjs";

import type { IconName } from "@similarweb/icons";
import type { AiTrackerReportsSentimentAnalysisOverviewResponse } from "@similarweb/pro-api-client";
import type { StrictColor } from "@similarweb/styles";
import type { INavItemBadgeType } from "@similarweb/ui-components";

import type { IAssetPermissions } from "app/services/asset-permissions/AssetPermissions";
import type { IWebsiteItem } from "pro-features/components/AutocompleteWebsites/types";

export type DatesRange = {
    from: Dayjs;
    to: Dayjs;
};

export interface ILLMReport {
    campaign: IAiTrackerCampaign;
    campaignConfig: ICampaignConfig;
    reportDuration: DatesRange;
    reportStatus: ReportStatus;
}

export enum EAvailableTabs {
    BRANDS = "brand",
    PROMPTS = "prompts",
    SOURCES = "sources",
    SENTIMENT = "sentiment",
    RECOMMENDATIONS = "recommendations",
}

export type SortDirection = "ascend" | "descend";
export type ConfigurationsDict = Partial<Record<LLModel, ICampaignConfig>>;

export interface IAiTrackerCampaign {
    id: string;
    name: string;
    brands: string[];
    createdDate: string;
    lastModifiedDate: string;
    createdByUserName: string;
    ownerUserName: string;
    configs: ConfigurationsDict;
    createdById: number;
    primaryDomain?: ICampaignDomain;
    domains?: ICampaignDomain[];
    topics: string[];
    countryCode: number;
    assetPermissions?: IAssetPermissions;
    includeSubdomains?: boolean;
    isDemo: boolean;
    category?: string;
    competitors: ICampaignCompetitor[];
    language: {
        cultureName: string;
        name: string;
        isRtl: boolean;
    };
    subscriptions?: string[];
}

export const AI_TRACKER_WEEKLY_REPORT_SUBSCRIPTION = "AiTrackerWeeklyReport";

export const isWeeklyReportSubscribed = (subscriptions?: string[]): boolean =>
    !!subscriptions?.includes(AI_TRACKER_WEEKLY_REPORT_SUBSCRIPTION);

export interface IAiTrackerCampaignDetails {
    campaignId: string;
    name: string;
    domains?: ICampaignDomain[];
    createdById: number;
    createDate: string;
    lastUpdatedDate: string;
    brands: string[];
    topics: IDetailedTopic[];
}

export interface IDetailedTopic {
    topicId: string;
    name: string;
    prompts: IDetailedPrompt[];
}

export interface IDetailedPrompt {
    promptId: string;
    topicId: string;
    query: string;
    intent: string;
    timestamp: string;
}

export interface ICampaignConfig {
    configId: string;
    firstScrapeDate: string;
    lastScrapeDate: string;
    status: CampaignStatus;
}

export interface ILLMReportWidget extends ILLMReport {}

export interface IAiModelConfig {
    displayNameKey: string;
    Icon: ComponentType<{ size?: number | string; color?: string; className?: string }>;
    iconName: IconName;
    color?: StrictColor;
    isPromotion?: boolean;
    isNotSupported?: boolean;
}

export type ExplicitLLModel = Exclude<LLModel, LLModel.All>;
export enum LLModel {
    All = "all",
    GPT = "chatgpt",
    AiMode = "google_ai_mode",
    Perplexity = "perplexity",
    Gemini = "gemini",
    Claude = "claude",
}

export enum ReportStatus {
    Error,
    Ready,
    NotReady,
}

export interface IIntent {
    label: string;
    color: StrictColor;
    textColor?: StrictColor;
    value?: number;
    text?: string;
}

export interface ITabData {
    title: string;
    tooltip?: string;
    stateId: string;
    badge?: INavItemBadgeType;
    firstSupportedDate?: Dayjs;
}

export interface ILLMWidgetRequestParams {
    campaignGuid: string;
    startDate: string; // YYYY-MM-DD
    endDate: string; // YYYY-MM-DD
    scrapingConfigurationGuids: string[];
    topics?: string[];
}

export enum SentimentStatus {
    Positive = "positive",
    Negative = "negative",
    Neutral = "neutral",
    NotMentioned = "not mentioned",
}

export enum VolatilityStatus {
    Stable = "Stable",
    Moderate = "Moderate",
    Volatile = "Volatile",
}

export enum CitationStatus {
    Cited = "Cited",
    NotCited = "Not Cited",
}

export enum CitationGapStatus {
    Full = "Full Gap",
    Partial = "Partial Gap",
    None = "No Gap",
}

export enum CampaignStatus {
    Completed = "Completed",
    Pending = "Pending",
    Failed = "Failed",
}

export interface ICampaignModalData {
    id: string;
    lastModifiedDate: string;
    name: string;
    domains?: ICampaignDomain[];
    brands?: string[];
    competitors?: ICampaignCompetitor[];
    subscriptions?: string[];
    assetPermissions?: IAssetPermissions;
    configs?: ConfigurationsDict;
    countryCode?: number;
    includeSubdomains?: boolean;
}

export interface ISentiment {
    positive: number;
    neutral: number;
    negative: number;
}

export type TopicSentiment = { topicName?: string } & ISentiment;
export type ISentimentOverviewResponse = AiTrackerReportsSentimentAnalysisOverviewResponse;

export interface ILanguage {
    id: string;
    name: string;
}

export interface IRawCompetitor {
    domain: IWebsiteItem | null;
    brands: string[];
}

export interface ICampaignCompetitor {
    domain: {
        url: string;
        favicon: string;
    };
    brands: string[];
}

export interface ICampaignDomain {
    url: string;
    favicon: string;
}
